import { CheckCircleFilled, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Flex, Modal, Popover, Radio, Row, Space, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useDetectLanguages, useLanguages } from '../../../api/Questions';
import { QuestionTypeValues } from '../../../constants';
import { surveyDetailsType } from '../../../types';
import LanguageSelect from './LanguageSelect';

const { Text, Title, Paragraph } = Typography;

const LANGUAGE_DETECTION_BATCH_SIZE = 10;

const LanguageCard = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
`;

const GlobalSettingsBox = styled.div`
  background: #f0f5ff;
  border: 1px solid #adc6ff;
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const TranslationBox = styled.div`
  background: ${({ $disabled }) => ($disabled ? '#f5f5f5' : '#ffffff')};
  border: 1px solid ${({ $disabled }) => ($disabled ? '#e5e7eb' : '#f0f0f0')};
  border-radius: 8px;
  padding: 16px 24px;
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  opacity: ${({ $disabled }) => ($disabled ? 0.7 : 1)};
`;

function LanguageSettings({
  surveyData,
  setSurveyData,
  prev,
  next,
  finishUpload,
  createSurveyLoading,
  hasNextStep,
}) {
  const { data: languages, isLoading: languagesLoading } = useLanguages();
  const detectLanguages = useDetectLanguages();

  const openEndedQuestions = surveyData.questions.filter(
    (q) => q.columnType === QuestionTypeValues.HAS_THEMES,
  );
  const translationChoice = surveyData?.surveyDetails?.translation;
  const isEnglishLanguage = (code) => {
    if (!code) return false;
    const normalized = String(code).toLowerCase();
    return normalized === 'en' || normalized.startsWith('en-');
  };
  const selectedLanguages = openEndedQuestions.map((q) => q.theme_language).filter(Boolean);
  const uniqueLanguages = [...new Set(selectedLanguages)];
  const allLanguagesSelected =
    openEndedQuestions.length > 0 && selectedLanguages.length === openEndedQuestions.length;
  const [singleLanguage] = uniqueLanguages;
  let globalLanguage = null;
  if (allLanguagesSelected && uniqueLanguages.length === 1) {
    globalLanguage = singleLanguage;
  } else if (allLanguagesSelected && uniqueLanguages.length > 1) {
    globalLanguage = 'multi';
  }
  const allSelectedEnglish =
    allLanguagesSelected && selectedLanguages.every((code) => isEnglishLanguage(code));
  const translationDisabled = allSelectedEnglish;
  const translationActive = translationChoice === true && !translationDisabled;

  useEffect(() => {
    if (translationDisabled) {
      setSurveyData((prevData) => ({
        ...prevData,
        surveyDetails: {
          ...(prevData?.surveyDetails || {}),
          translation: false,
        },
      }));
    }
  }, [translationDisabled, setSurveyData]);

  const handleTranslationChange = (event) => {
    const selection = event?.target?.value;
    setSurveyData((prevData) => ({
      ...prevData,
      surveyDetails: {
        ...(prevData?.surveyDetails || {}),
        translation: selection,
      },
    }));
  };

  const detectionKey = openEndedQuestions
    .map(
      (q) =>
        `${q.index}:${q.detected_language_code ? 1 : 0}:${q.language_detection_pending ? 1 : 0}:${
          q.detectionSamples?.length || 0
        }`,
    )
    .join('|');

  // Trigger detection for any question that needs it
  useEffect(() => {
    const questionsToDetect = openEndedQuestions.filter(
      (q) =>
        !q.detected_language_code &&
        !q.language_detection_pending &&
        q.detectionSamples?.length > 0,
    );

    const batch = questionsToDetect.slice(0, LANGUAGE_DETECTION_BATCH_SIZE);

    if (batch.length > 0) {
      // Mark as pending to avoid double triggering
      setSurveyData((prevData) => ({
        ...prevData,
        questions: prevData.questions.map((q) =>
          batch.some((td) => td.index === q.index) ? { ...q, language_detection_pending: true } : q,
        ),
      }));

      const detectionPayload = batch.map((question) => ({
        index: question.index,
        is_open_ended: true,
        responses: question.detectionSamples,
      }));

      detectLanguages.mutate(
        { questions: detectionPayload },
        {
          onSuccess: (data) => {
            const resultMap = new Map((data?.data || []).map((result) => [result.index, result]));
            setSurveyData((prevData) => ({
              ...prevData,
              questions: prevData.questions.map((q) => {
                const result = resultMap.get(q.index);
                if (result) {
                  const detectedLanguage = result.language_code;
                  const shouldAutoSet =
                    !q.theme_language && !result.needs_review && detectedLanguage;
                  return {
                    ...q,
                    detected_language_code: detectedLanguage,
                    detected_language_confidence: result.confidence,
                    language_detection_pending: false,
                    language_needs_review: result.needs_review,
                    language_sample_count: result.detected_samples,
                    language_total_samples: result.total_samples,
                    theme_language: shouldAutoSet ? detectedLanguage : q.theme_language,
                  };
                }
                if (batch.some((td) => td.index === q.index)) {
                  return { ...q, language_detection_pending: false };
                }
                return q;
              }),
            }));
          },
          onError: () => {
            setSurveyData((prevData) => ({
              ...prevData,
              questions: prevData.questions.map((q) =>
                batch.some((td) => td.index === q.index)
                  ? { ...q, language_detection_pending: false }
                  : q,
              ),
            }));
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectionKey]);

  const handleGlobalLanguageChange = (value) => {
    const selection = !isEnglishLanguage(value);
    setSurveyData((prevData) => ({
      ...prevData,
      questions: prevData.questions.map((q) =>
        q.columnType === QuestionTypeValues.HAS_THEMES ? { ...q, theme_language: value } : q,
      ),
      surveyDetails: {
        ...(prevData?.surveyDetails || {}),
        translation: selection,
      },
    }));
  };

  const handleLanguageChange = (index, value) => {
    const updatedLanguages = surveyData.questions.map((q) =>
      q.index === index ? value : q.theme_language,
    );
    const allEnglish = updatedLanguages.every((code) => isEnglishLanguage(code));
    setSurveyData((prevData) => ({
      ...prevData,
      questions: prevData.questions.map((q) =>
        q.index === index ? { ...q, theme_language: value } : q,
      ),
      surveyDetails: {
        ...(prevData?.surveyDetails || {}),
        translation: !allEnglish,
      },
    }));
  };

  const onFinish = () => {
    // Validate that all open-ended questions have a language selected
    const missingLanguage = openEndedQuestions.some((q) => !q.theme_language);
    if (missingLanguage) {
      Modal.error({
        title: 'Language selection required',
        content: `Please select a ${
          translationActive ? 'source ' : ''
        }language for all open-ended questions.`,
      });
      return;
    }

    if (translationChoice === null || translationChoice === undefined) {
      Modal.error({
        title: 'Translation preference required',
        content: 'Please choose whether you want to translate responses to English.',
      });
      return;
    }

    if (hasNextStep) {
      next();
    } else {
      finishUpload();
    }
  };

  const getLanguageName = (code) => {
    if (!code) return code;
    if (code === 'multi') return 'Multiple languages';

    const lang = languages?.data?.find((l) => l.language_code === code || l.code === code);
    if (lang) {
      return lang.display_name || lang.name || code;
    }

    try {
      const displayName = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
      return displayName || code;
    } catch {
      return code;
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={3}>Languages</Title>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>
          Confirm the response language for each open-ended question
        </Text>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          We’ve automatically detected the language used in the responses to each question. Please
          review and adjust if needed to ensure accurate theme generation and coding.
          <Tooltip
            placement="top"
            overlayStyle={{ maxWidth: 400 }}
            title={
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>If responses are mostly one language, select that language.</li>
                <li>
                  If responses contain a mix with no dominant language, select “Multiple languages”
                  and Fathom will detect the language for each response.
                </li>
              </ul>
            }
          >
            <InfoCircleOutlined style={{ marginLeft: 8, color: 'rgba(0, 0, 0, 0.45)' }} />
          </Tooltip>
        </Paragraph>
      </div>

      {openEndedQuestions.length > 1 && (
        <GlobalSettingsBox>
          <Flex vertical gap={4}>
            <Text strong>Set language for all questions</Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              This will override individual selections below.
            </Text>
          </Flex>
          <LanguageSelect
            languages={languages?.data}
            loading={languagesLoading}
            placeholder="Select language"
            value={globalLanguage}
            onChange={handleGlobalLanguageChange}
            style={{ width: 250 }}
            data-cy="language-select-global"
          />
        </GlobalSettingsBox>
      )}

      <Flex vertical gap={16}>
        {openEndedQuestions.map((question) => {
          const sampleResponse = question.popOverResponses?.reduce(
            (longest, current) =>
              (current?.response?.length || 0) > (longest?.response?.length || 0)
                ? current
                : longest,
            question.popOverResponses?.[0],
          );
          const sampleText = sampleResponse?.response || 'No sample available';
          const isDetecting = question.language_detection_pending;
          const sampleCount = question.language_sample_count || 0;
          const detectedCode = question.detected_language_code;
          const isMatch = detectedCode && question.theme_language === detectedCode;
          const questionNumber = Number.parseInt(question.index, 10) + 1;

          const samplePopoverContent = (
            <div style={{ maxWidth: 520 }}>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                Sample response
              </Text>
              <div style={{ marginTop: 6 }}>
                <Text>{sampleText}</Text>
              </div>
            </div>
          );

          return (
            <LanguageCard key={question.index}>
              <Flex align="center" justify="space-between" gap={16} style={{ width: '100%' }}>
                <Flex vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Question {questionNumber}
                  </Text>
                  <Text strong style={{ fontSize: 14 }} ellipsis>
                    {question.updated_question_name}
                  </Text>

                  <Flex gap={10} align="center" style={{ minHeight: 18 }}>
                    {isDetecting ? (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Detecting language…
                      </Text>
                    ) : (
                      <>
                        {isMatch ? (
                          <>
                            <CheckCircleFilled style={{ color: '#16a34a' }} />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Auto-detected ({sampleCount} rows)
                            </Text>
                          </>
                        ) : (
                          detectedCode && (
                            <>
                              <InfoCircleOutlined style={{ color: '#faad14' }} />
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                Detected: {getLanguageName(detectedCode)}
                              </Text>
                            </>
                          )
                        )}

                        <Popover
                          content={samplePopoverContent}
                          title={null}
                          placement="topLeft"
                          trigger="click"
                        >
                          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
                            View sample
                          </Button>
                        </Popover>
                      </>
                    )}
                  </Flex>
                </Flex>

                <div style={{ width: 280 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, display: 'block', marginBottom: 6 }}
                  >
                    {translationActive ? 'Source language' : 'Language'}
                  </Text>
                  <LanguageSelect
                    languages={languages?.data}
                    loading={languagesLoading}
                    placeholder="Select language"
                    value={question.theme_language}
                    onChange={(value) => handleLanguageChange(question.index, value)}
                    style={{ width: '100%', height: 40 }}
                    disabled={isDetecting}
                    data-cy="language-select-question"
                  />
                </div>
              </Flex>
            </LanguageCard>
          );
        })}
      </Flex>

      <TranslationBox $disabled={translationDisabled}>
        <Flex vertical gap={4} style={{ flex: 1 }}>
          <Text strong>Translate responses to English</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {translationDisabled
              ? 'All selected languages are English, so translation is disabled.'
              : 'Choose whether you want responses translated to English before analysis.'}
          </Text>
        </Flex>
        <Radio.Group
          onChange={handleTranslationChange}
          value={surveyData?.surveyDetails?.translation}
          disabled={translationDisabled}
        >
          <Space direction="vertical">
            <Radio value>Yes, translate responses to English</Radio>
            <Radio value={false}>No, I don’t want to translate to English</Radio>
          </Space>
        </Radio.Group>
      </TranslationBox>

      <Row justify="end" style={{ marginTop: 32 }}>
        <Space>
          <Button onClick={prev} disabled={createSurveyLoading}>
            Back
          </Button>
          <Button
            type="primary"
            onClick={onFinish}
            loading={createSurveyLoading}
            disabled={createSurveyLoading}
          >
            {hasNextStep ? 'Continue' : 'Finish upload'}
          </Button>
        </Space>
      </Row>
    </div>
  );
}

LanguageSettings.propTypes = {
  surveyData: surveyDetailsType.isRequired,
  setSurveyData: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  next: PropTypes.func.isRequired,
  finishUpload: PropTypes.func.isRequired,
  createSurveyLoading: PropTypes.bool.isRequired,
  hasNextStep: PropTypes.bool.isRequired,
};

export default LanguageSettings;
